import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { SignaturePad } from '../components/SignaturePad';
import { FormFieldReadOnly } from '../components/FormFieldRenderer';
import { filterVisibleFields, evaluateVisibility } from '../lib/formVisibility';
import { groupFieldsIntoSections } from '../lib/formSections';
import { Loader2, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Customer Form Review (Public, Token-Scoped, V1)
 *
 * Reachable at /customer/forms/:token without authentication. Validates the
 * token via the backend, renders the completed form read-only, captures a
 * signer name + canvas signature, then locks the form on submit.
 */
export function CustomerFormReview() {
  const { token } = useParams();
  const [state, setState] = useState({
    loading: true,
    error: null, // { code, message }
    form: null,
    signed: false,
  });
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/customer/forms/${token}`);
        if (cancelled) return;
        setState({ loading: false, error: null, form: res.data, signed: false });
        if (res.data?.signer_email_hint) setSignerEmail(res.data.signer_email_hint);
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        const errObj = (detail && typeof detail === 'object')
          ? detail
          : { code: 'ERROR', message: typeof detail === 'string' ? detail : 'Could not load form.' };
        setState({ loading: false, error: errObj, form: null, signed: false });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!signerName.trim() || signerName.trim().length < 2) {
      toast.error('Please enter your full name.');
      return;
    }
    if (!signature) {
      toast.error('Please sign in the box above.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/customer/forms/${token}/sign`, {
        signer_name: signerName.trim(),
        signer_email: signerEmail.trim() || null,
        signature_data_url: signature,
      });
      setState(prev => ({ ...prev, signed: true }));
      toast.success('Thank you — your signature has been recorded.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) ? detail.message
        : (typeof detail === 'string' ? detail : 'Could not submit signature. Please try again.');
      toast.error(msg);
    }
    setSubmitting(false);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full" data-testid="customer-form-error">
          <CardContent className="py-10 text-center space-y-3">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
            <h1 className="text-lg font-semibold text-slate-900">{titleForCode(state.error.code)}</h1>
            <p className="text-sm text-slate-600">{state.error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md w-full" data-testid="customer-form-success">
          <CardContent className="py-10 text-center space-y-3">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h1 className="text-lg font-semibold text-slate-900">Signature received</h1>
            <p className="text-sm text-slate-600">
              Thank you, <span className="font-medium">{signerName}</span>. Your signature has been recorded
              and a copy is now saved with {state.form?.company_name || 'the company'}.
            </p>
            <p className="text-xs text-slate-400 pt-2">You can close this window.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const f = state.form;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4" data-testid="customer-form-review">
        {/* Branding header */}
        <div className="text-center space-y-1">
          <h1 className="text-xl font-bold text-slate-900">{f.company_name}</h1>
          <p className="text-sm text-slate-500">Form ready for your review &amp; signature</p>
        </div>

        {/* Form card */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  {f.form_name}
                </CardTitle>
                {f.job_reference && (
                  <p className="text-xs text-slate-500 mt-1">Job reference: <span className="font-mono">{f.job_reference}</span></p>
                )}
                {f.customer_name && (
                  <p className="text-xs text-slate-500">Customer: {f.customer_name}</p>
                )}
              </div>
              <Badge variant="outline" className="text-[10px]">Read-only</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-5 space-y-4" data-testid="customer-form-fields">
            {(f.fields || []).length === 0 ? (
              <p className="text-sm text-slate-500 italic">This form has no fields.</p>
            ) : (
              groupFieldsIntoSections(f.fields).map((group, gi) => (
                <CustomerSectionGroup
                  key={group.section ? group.section.field_key : `g${gi}`}
                  group={group}
                  data={f.data || {}}
                />
              ))
            )}
          </CardContent>
        </Card>

        {/* Signature card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your signature</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="signer-name" className="text-xs">Full name <span className="text-rose-500">*</span></Label>
                  <Input
                    id="signer-name"
                    value={signerName}
                    onChange={e => setSignerName(e.target.value)}
                    placeholder="Type your full name"
                    required
                    minLength={2}
                    maxLength={200}
                    disabled={submitting}
                    data-testid="signer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="signer-email" className="text-xs">Email <span className="text-slate-400">(optional)</span></Label>
                  <Input
                    id="signer-email"
                    type="email"
                    value={signerEmail}
                    onChange={e => setSignerEmail(e.target.value)}
                    placeholder="you@example.com"
                    maxLength={255}
                    disabled={submitting}
                    data-testid="signer-email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Signature <span className="text-rose-500">*</span></Label>
                <div className="mt-1.5">
                  <SignaturePad
                    onSignatureChange={setSignature}
                    disabled={submitting}
                    width={typeof window !== 'undefined' ? Math.min(640, window.innerWidth - 64) : 480}
                    height={180}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !signerName.trim() || !signature}
                data-testid="customer-submit-signature-btn"
              >
                {submitting ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</>) : 'Sign & Submit'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {f.expires_at && (
          <p className="text-center text-[11px] text-slate-400">
            This secure link expires {new Date(f.expires_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Phase 3 — Repeating Sections (read-only customer view) ──────────────
function CustomerSectionGroup({ group, data }) {
  const section = group.section;
  // Phase 2.x: hide entire section when section_header.visible_if fails.
  if (section && !evaluateVisibility(section, data, [])) {
    return null;
  }
  const headerNode = section ? (
    <FormFieldReadOnly field={section} value={null} />
  ) : null;

  if (!group.repeatable) {
    const visible = filterVisibleFields(group.fields, data);
    return (
      <div className="space-y-3">
        {headerNode}
        {visible.map(f => (
          <FormFieldReadOnly key={f.field_key} field={f} value={data[f.field_key]} />
        ))}
      </div>
    );
  }

  const sectionKey = section.field_key;
  const instances = Array.isArray(data[sectionKey]) ? data[sectionKey] : [];
  return (
    <div className="space-y-3" data-testid={`customer-repeat-section-${sectionKey}`}>
      {headerNode}
      {instances.length === 0 ? (
        <p className="text-xs text-slate-400 italic px-1">
          No {(group.label || 'instance').toLowerCase()}s recorded.
        </p>
      ) : (
        instances.map((inst, idx) => {
          const merged = { ...data, ...(inst || {}) };
          const visible = filterVisibleFields(group.fields, merged);
          return (
            <div
              key={`${sectionKey}-${idx}`}
              className="rounded-lg border border-violet-200 bg-violet-50/30 p-3 space-y-3"
              data-testid={`customer-repeat-instance-${sectionKey}-${idx}`}
            >
              <p className="text-xs font-semibold text-violet-900">
                {group.label || 'Instance'} {idx + 1}
              </p>
              {visible.map(f => (
                <FormFieldReadOnly key={f.field_key} field={f} value={(inst || {})[f.field_key]} />
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}


function titleForCode(code) {
  switch (code) {
    case 'EXPIRED': return 'Link expired';
    case 'CANCELED': return 'Request canceled';
    case 'REOPENED': return 'Form reopened';
    case 'ALREADY_SIGNED': return 'Already signed';
    case 'INVALID_LINK': return 'Invalid link';
    default: return 'Cannot open form';
  }
}

export default CustomerFormReview;
